/**
 * Layout reference
 * Description: Layout is an Object/Array that represents an element and can
 *              have the next structure, see LayoutExample.
 */

/* LayoutExample1 represents Object - one element */
const LayoutExample1 = {
    // element name from elements/index.js - default "Block"
    name: "BlockHeader",
    // info (optional) - object
    info: {},
    // props object (optional) - passed to element (component) "Block"
    props: { className: ["ignore-global", "as-odd"] },
    // children array (optional) - passed to element (component) "Block"
    children: [
        // This is also layout
        {
            name: "ProjectTitle",
        },

        // This is an array from which one of the layouts will be taken
        [
            {
                name: "ImageVariant1",
            },

            {
                name: "ImageVariant2",
            },

            {
                name: "ImageVariant3",
            },
        ],
    ],
};

/**
 * LayoutExample2 represents Array of elements from which one of the layouts
 * will be taken randomly
 */
const LayoutExample2 = [
    {
        name: "BlockHeaderVariant1",
    },

    {
        name: "BlockHeaderVariant2",
    },

    {
        name: "BlockHeaderVariant3",
    },

    {
        name: "BlockHeaderVariant4",
    },

    // Can use exists layout's
    LayoutExample1,
];

// -------------------------------------------------------------------------- //
import { rand } from "../../helpers/generators.js";

function Project(children) {
    return {
        name: "BlockProject",
        rules: {
            evenOdd: true,
            intersect: true,
        },
        children,
    };
}

function Menu() {
    return {
        name: "BlockMenu",
        props: { className: ["ignore-global", "as-odd"], ignoreIntersect: true },
        children: [
            {
                name: "Logotype",
                props: {
                    useTitle: true,
                    className: ["ignore-global", "filter-brightness"],
                },
            },

            {
                name: "MenuLinks",
                props: { useBlocks: true },
            },

            {
                name: "BurgerMenu",
                children: [
                    {
                        name: "Logotype",
                        props: {
                            useTitle: true,
                            mobile: true,
                            className: ["ignore-global"],
                        },
                    },
                    {
                        name: "MenuLinks",
                        props: { useBlocks: true, mobile: true },
                    },

                    {
                        name: "MenuActions",
                        props: { mobile: true, className: ["grid-column", "grid-gap-32", "padding-x-16"] },
                    },
                ],
            },
        ],
    };
}

function EventHeader() {
    return {
        id: "Home",
        name: "BlockHeader",
        props: { ignoreIntersect: true },
        children: [
            {
                name: "BlockHeaderContent",
                info: { BlockHeaderContentPosition: "vertical", gradient: 0 },
                props: { className: ["grid-center", "grid-gap-16", "w1200px", "text-center"] },
                children: [
                    {
                        name: "HeaderEvent",
                        props: {
                            useVariable: "Stimulus",
                            style: { fontSize: "48px", lineHeight: "1", alignSelf: "flex-end" },
                        },
                    },

                    {
                        name: "BlockHeaderButtons",
                        props: { position: "center" },
                    },

                    { name: "BlockHeaderImageVertical" },
                ],
            },
        ],
    };
}

function Header() {
    return {
        id: "Home",
        name: "BlockHeader",
        props: { ignoreIntersect: true },
        children: [[HeaderContentVariant1(), HeaderContentVariant2(), HeaderContentVariant3()]],
    };
}

// Column
function HeaderContentVariant1() {
    const info = {
        BlockHeaderContentPosition: "vertical",
        gradient: 0,
    };

    return {
        name: "BlockHeaderContent",
        info,
        props: { className: ["grid-center", "grid-gap-16", "w1200px", "text-center"], ...info },
        children: [
            {
                name: "Header1",
                props: {
                    useVariable: "Stimulus",
                    style: { fontSize: "48px", lineHeight: "1", alignSelf: "flex-end" },
                },
            },

            {
                name: "BlockHeaderButtons",
                props: { position: "center" },
            },

            { name: "BlockHeaderImageVertical" },
        ],
    };
}

// Row: Text - Image
function HeaderContentVariant2() {
    const info = {
        BlockHeaderContentPosition: "text-image",
        gradient: 1,
    };

    return {
        name: "BlockHeaderContent",
        info,
        props: {
            className: ["text-left"],
            ...info,
        },
        children: [
            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "Header1",
                        props: {
                            useVariable: "Stimulus",
                        },
                    },

                    {
                        name: "BlockHeaderButtons",
                        props: { position: "left" },
                    },
                ],
            },

            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "BlockHeaderImageHorizontal",
                        props: { position: "right" },
                    },
                ],
            },
        ],
    };
}

// Row: Image - Text
function HeaderContentVariant3() {
    const info = {
        BlockHeaderContentPosition: "image-text",
        gradient: 1,
    };

    return {
        name: "BlockHeaderContent",
        info,
        props: { className: ["text-right"], ...info },
        children: [
            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "BlockHeaderImageHorizontal",
                        props: { position: "left" },
                    },
                ],
            },

            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "Header1",
                        props: { useVariable: "Stimulus" },
                    },

                    {
                        name: "BlockHeaderButtons",
                        props: { position: "right" },
                    },
                ],
            },
        ],
    };
}

function Embed() {
    return {
        id: "Embed",
        name: "BlockEmbed",
    };
}

function Video() {
    return {
        id: "Video",
        name: "BlockVideo",
    };
}

function Features() {
    const variants = [1, 2, 3, 4];
    const children = variants.map(variant => ({
        name: "BlockFeatures",
        props: {
            variant,
        },
    }));

    return {
        id: "Features",
        children: [children],
    };
}

function Steps() {
    const variants = [1, 2, 3, 4];
    const children = variants.map(variant => ({
        name: "BlockSteps",
        props: {
            variant,
        },
    }));

    return {
        id: "Steps",
        children: [children],
    };
}

function Feedbacks() {
    const variants = [1, 2];
    const children = variants.map(variant => ({
        name: "BlockFeedbacks",
        props: {
            variant,
        },
    }));

    return {
        id: "Feedbacks",
        children: [children],
    };
}

function Numbers() {
    return {
        id: "Numbers",
        children: [{ name: "BlockNumbers" }],
    };
}

function Slider() {
    return {
        id: "Slider",
        name: "BlockSlider",
    };
}

// function Offers() {
//     const sellingPlans = [1, 2, 3];
//     const trafficAcquisition = [1, 2, 3];

//      // TODO! LPB-569-temporary-disable-reverse-offers
//     //reverse: rand(0, 1), // SellingPlans
//     return {
//         id: "Offers",
//         name: "BlockOffers",
//         children: [
//             sellingPlans.map(variant => ({
//                 name: "OffersSellingPlans",
//                 variant,
//             })),
//             trafficAcquisition.map(variant => ({
//                 name: "OffersTrafficAcquisition",
//                 variant,
//             })),
//         ],
//     };
// }

function Offers() {
    const _ = {
        props: {
            variant: rand(1, 3), // SellingPlans
            // TODO! LPB-569-temporary-disable-reverse-offers
            //reverse: rand(0, 1), // SellingPlans
        },
    };

    return {
        id: "Offers",
        name: "BlockOffers",
        children: [
            { name: "OffersSellingPlans", ..._ },
            { name: "OffersTrafficAcquisition", ..._ },
        ],
    };
}

function ProductDescription() {
    return {
        id: "ProductDescription",
        name: "BlockProductDescription",
    };
}

function Delivery() {
    return {
        id: "Delivery",
        name: "BlockDelivery",
    };
}

function GetInTouch() {
    return {
        id: "GetInTouch",
        name: "BlockGetInTouch",
    };
}

function Footer() {
    return {
        name: "BlockFooter",
        children: [
            {
                name: "BlockGridRow",
                props: {
                    style: {
                        gridTemplateColumns: "1fr max-content",
                        maxWidth: "1200px",
                        margin: "auto",
                    },
                },
                children: [
                    {
                        name: "BlockGridColumn",
                        props: { style: { gridGap: "32px", justifySelf: "flex-start" } },
                        children: [{ name: "Socials" }, { name: "Copyright" }],
                    },
                    {
                        name: "MenuLinks",
                        props: { useBlocks: true },
                    },
                    { name: "PaymentLogos" },
                    { name: "TermsOfUse" },
                ],
            },
        ],
    };
}

function CookiesPopup() {
    return {
        name: "CookiesPopup",
    };
}

function Timer() {
    return {
        name: "Timer",
    };
}

function HeaderEcommerce() {
    return {
        id: "Home",
        name: "BlockHeaderECommerce",
        props: { ignoreIntersect: true },
        children: [[HeaderEcommerceContentVariant2(), HeaderEcommerceContentVariant3()]],
    };
}

// Row: Text - Image
function HeaderEcommerceContentVariant2() {
    const info = {
        BlockHeaderContentPosition: "text-image",
        gradient: 1,
        ProductUp: true,
    };

    return {
        name: "BlockHeaderContent",
        info,
        props: {
            className: ["text-left"],
            ...info,
        },
        children: [
            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "Header1",
                        props: {
                            useVariable: "Stimulus",
                        },
                    },

                    {
                        name: "BlockHeaderEcommerceButtons",
                        props: { position: "left" },
                    },
                ],
            },

            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "BlockECommerceSlider",
                        props: { position: "right" },
                    },
                ],
            },
        ],
    };
}

// Row: Image - Text
function HeaderEcommerceContentVariant3() {
    const info = {
        BlockHeaderContentPosition: "image-text",
        gradient: 1,
        ProductUp: true,
    };

    return {
        name: "BlockHeaderContent",
        info,
        props: { className: ["text-right"], ...info },
        children: [
            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "BlockECommerceSlider",
                        props: { position: "right" },
                    },
                ],
            },

            {
                props: { className: ["grid-gap-32"] },
                children: [
                    {
                        name: "Header1",
                        props: { useVariable: "Stimulus" },
                    },

                    {
                        name: "BlockHeaderEcommerceButtons",
                        props: { position: "right" },
                    },
                ],
            },
        ],
    };
}

function BlockECommerce() {
    return {
        name: "BlockECommerce",
        props: { ignoreIntersect: true },
        children: [[HeaderEcommerceContentVariant3()]],
    };
}

export const Layouts = {
    Project,

    Menu,
    Header,
    EventHeader,
    HeaderContentVariant1,
    HeaderContentVariant2,
    HeaderContentVariant3,
    HeaderEcommerce,

    Embed,
    Video,

    Steps,
    Features,
    ProductDescription,
    Feedbacks,
    Numbers,
    Slider,
    Offers,

    BlockECommerce,
    GetInTouch,
    Delivery,
    Footer,
    CookiesPopup,
    Timer,
};
